import React, { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import toast from "react-hot-toast";
import Organisation from "@/model/interface/Organisation";
import { MarkupType, PricingPlan } from "@/model/interface/PricingPlan";
import {
  useGetOrganisationUsageRatesQuery,
  useSetOrganisationUsageRatesMutation,
  useClearOrganisationUsageRatesMutation,
} from "@/store/admin_api";

const USAGE_PLAN_PREFIX = "Custom pricing for ";

function effectiveRate(base: number, type: MarkupType, amount: number): number {
  return type === "PERCENTAGE" ? base + (base * amount) / 100 : base + amount;
}

type RateKey = "outboundCall" | "inboundCall" | "outboundSms" | "inboundSms" | "phoneNumber" | "tollfreeNumber" | "aiVoice";

const USAGE_FIELDS: { key: RateKey; label: string; unit: string }[] = [
  { key: "outboundCall", label: "Outbound voice", unit: "/min" },
  { key: "inboundCall", label: "Inbound voice", unit: "/min" },
  { key: "outboundSms", label: "Outbound SMS", unit: "/segment" },
  { key: "inboundSms", label: "Inbound SMS", unit: "/segment" },
  { key: "phoneNumber", label: "Phone number", unit: "/month" },
  { key: "tollfreeNumber", label: "Toll free number", unit: "/month" },
  { key: "aiVoice", label: "AI voice", unit: "/min" },
];

function effectiveRatesOf(plan: PricingPlan): Record<RateKey, number> {
  return {
    outboundCall: effectiveRate(plan.outboundCallBaseRate, plan.outboundCallMarkupType, plan.outboundCallMarkupAmount),
    inboundCall: effectiveRate(plan.inboundCallBaseRate, plan.inboundCallMarkupType, plan.inboundCallMarkupAmount),
    outboundSms: effectiveRate(plan.outboundSmsBaseRate, plan.outboundSmsMarkupType, plan.outboundSmsMarkupAmount),
    inboundSms: effectiveRate(plan.inboundSmsBaseRate, plan.inboundSmsMarkupType, plan.inboundSmsMarkupAmount),
    phoneNumber: effectiveRate(plan.phoneNumberBaseMonthlyRate, plan.phoneNumberMarkupType, plan.phoneNumberMarkupAmount),
    tollfreeNumber: effectiveRate(plan.tollfreeNumberBaseMonthlyRate, plan.phoneNumberMarkupType, plan.phoneNumberMarkupAmount),
    aiVoice: effectiveRate(plan.vapiBaseRate, plan.vapiMarkupType, plan.vapiMarkupAmount),
  };
}

export default function OrgUsageRatesSection({ org }: { org: Organisation }) {
  const { data: plan, isLoading } = useGetOrganisationUsageRatesQuery(org.id ?? "", { skip: !org.id });
  const [setOrganisationUsageRates, { isLoading: isSaving }] = useSetOrganisationUsageRatesMutation();
  const [clearOrganisationUsageRates, { isLoading: isClearing }] = useClearOrganisationUsageRatesMutation();

  const [draft, setDraft] = useState<Record<string, string>>({});

  const overridden = plan?.name?.startsWith(USAGE_PLAN_PREFIX) === true;

  useEffect(() => {
    if (plan) {
      const rates = effectiveRatesOf(plan);
      setDraft(Object.fromEntries(Object.entries(rates).map(([k, v]) => [k, String(v)])));
    }
  }, [plan]);

  const handleSave = async () => {
    if (!plan) {
      return;
    }
    const flat: MarkupType = "FLAT";
    const num = (k: string) => Math.max(0, parseFloat(draft[k] ?? "0") || 0);
    const updated: PricingPlan = {
      ...plan,
      outboundCallBaseRate: num("outboundCall"),
      outboundCallMarkupType: flat,
      outboundCallMarkupAmount: 0,
      inboundCallBaseRate: num("inboundCall"),
      inboundCallMarkupType: flat,
      inboundCallMarkupAmount: 0,
      outboundSmsBaseRate: num("outboundSms"),
      outboundSmsMarkupType: flat,
      outboundSmsMarkupAmount: 0,
      inboundSmsBaseRate: num("inboundSms"),
      inboundSmsMarkupType: flat,
      inboundSmsMarkupAmount: 0,
      phoneNumberBaseMonthlyRate: num("phoneNumber"),
      phoneNumberMarkupType: flat,
      phoneNumberMarkupAmount: 0,
      tollfreeNumberBaseMonthlyRate: num("tollfreeNumber"),
      vapiBaseRate: num("aiVoice"),
      vapiMarkupType: flat,
      vapiMarkupAmount: 0,
      elevenLabsConvaiBaseRate: num("aiVoice"),
      elevenLabsConvaiMarkupType: flat,
      elevenLabsConvaiMarkupAmount: 0,
      nativeAiBaseRate: num("aiVoice"),
      nativeAiMarkupType: flat,
      nativeAiMarkupAmount: 0,
    };
    try {
      await setOrganisationUsageRates({ organisationId: org.id!, plan: updated }).unwrap();
      toast.success("Usage rates updated");
    } catch {
      toast.error("Failed to update usage rates");
    }
  };

  const handleReset = async () => {
    try {
      await clearOrganisationUsageRates(org.id!).unwrap();
      toast.success("Reverted to default usage rates");
    } catch {
      toast.error("Failed to reset usage rates");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-medium">Usage rates</h2>
        {overridden && <Badge variant="secondary">Custom</Badge>}
        {!overridden && plan && (
          <span className="text-sm text-muted-foreground">
            {plan.isDefault ? "Default rates" : `From the ${plan.name} rates`}
          </span>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {USAGE_FIELDS.map((field) => (
              <div key={field.key} className="space-y-1">
                <Label htmlFor={`usage-${field.key}`}>
                  {field.label} <span className="text-muted-foreground">({field.unit})</span>
                </Label>
                <Input
                  id={`usage-${field.key}`}
                  type="number"
                  min={0}
                  step="0.001"
                  value={draft[field.key] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save usage rates"}
            </Button>
            {overridden && (
              <Button variant="outline" onClick={handleReset} disabled={isClearing}>
                Reset to default
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
